package com.macrofocus.trenddisplay.timeseriesplot

import com.macrofocus.common.filter.Filter
import com.macrofocus.common.properties.*
import com.macrofocus.common.selection.MutableSelection
import com.macrofocus.common.selection.MutableSingleSelection
import com.macrofocus.common.selection.PropertySingleSelection
import com.macrofocus.high_d.axis.AxisModel
import com.macrofocus.high_d.axis.group.AxisGroupModel
import com.macrofocus.high_d.axis.group.DefaultAxisGroupModel
import com.macrofocus.trenddisplay.TrendDisplayLogic
import org.mkui.coloring.MutableColoring
import org.mkui.colormap.ColorMapFactory
import org.mkui.colormapping.MutableColorMapping
import org.mkui.component.CPFactory
import org.mkui.visual.Visual
import org.mkui.visual.VisualObjects
import org.molap.aggregates.cube.Group
import org.molap.dataframe.DataFrame
import org.molap.dataframe.DataFrameEvent
import org.molap.dataframe.DataFrameListener
import org.molap.subset.SubsetDataFrame

class DefaultTimeSeriesPlotModel<Row, Column>(
    factory: CPFactory,
    colorMapFactory: ColorMapFactory?,
    logic: TrendDisplayLogic<Row, Column, *>
) :
    AbstractTimeSeriesPlotModel<Row, Column>(), VisualObjects<Row> {
    enum class PropertyType {
        Time,
        Y,
        Size,
        Id
    }

    private val properties: MutableProperties<PropertyType> = EnumProperties<PropertyType>(
        PropertyType.values()
    )
    private val timeProperty: MutableProperty<Column?> = properties.createProperty<Column?>(PropertyType.Time, null)
    private val yProperty: MutableProperty<Column?> = properties.createProperty<Column?>(PropertyType.Y, null)
    private val sizeProperty: MutableProperty<Column?> = properties.createProperty(PropertyType.Size, null)
    private val idProperty: MutableProperty<Column?> = properties.createProperty(PropertyType.Id, null)

    private val logic: com.macrofocus.trenddisplay.TrendDisplayLogic<Row, Column, *> = logic

    override val dataFrame: DataFrame<Row, Column, *>
    override val annotationDataFrame: DataFrame<*, Column, *>?

    override val time: MutableSingleSelection<AxisModel<Row, Column>?>
    override val y: MutableSingleSelection<AxisModel<Row, Column>?>
    override val size: MutableSingleSelection<AxisModel<Row, Column>?>
    override val id: MutableSingleSelection<AxisModel<Row, Column>?>

    private val root: AxisGroupModel<Row, Column>
    override var timeAxisModel: AxisModel<Row, Column>? = null
        private set
    override var yAxisModel: AxisModel<Row, Column>? = null
        private set
    override var sizeAxisModel: AxisModel<Row, Column>?

    private val cache: List<DataFrame<Row, Column, *>> = ArrayList<DataFrame<Row, Column, *>>()

    init {
        properties.replaceProperty<Column?>(PropertyType.Id, logic.getSettings().id)
        properties.replaceProperty<Column?>(PropertyType.Time, logic.getSettings().time)

        this.dataFrame = logic.getDataFrame()
        this.annotationDataFrame = null

        this.root = DefaultAxisGroupModel(
            "Root",
            dataFrame,
            null,
            logic.getVisual().probing,
            logic.getVisual().selection,
            logic.getVisual().filter
        )

        time = PropertySingleSelection<AxisModel<Row, Column>?>(AxisModelProperty<Row, Column>(root, timeProperty))
        y = PropertySingleSelection<AxisModel<Row, Column>?>(AxisModelProperty<Row, Column>(root, yProperty))
        size = PropertySingleSelection<AxisModel<Row, Column>?>(AxisModelProperty<Row, Column>(root, sizeProperty))
        id = PropertySingleSelection<AxisModel<Row, Column>?>(AxisModelProperty<Row, Column>(root, idProperty))

        // Assign numerical columns
        for (axisModel in root.axisOrder!!) {
            if (axisModel.isNumerical) {
                if (timeAxisModel == null) {
                    timeAxisModel = axisModel
                } else if (yAxisModel == null) {
                    yAxisModel = axisModel
                    break
                }
            }
        }

        // If two numerical columns couldn't be found, then find categorical columns
        if (yAxisModel == null) {
            for (axisModel in root.axisOrder!!) {
                if (!axisModel.isNumerical) {
                    if (timeAxisModel == null) {
                        timeAxisModel = axisModel
                    } else if (yAxisModel == null) {
                        yAxisModel = axisModel
                        break
                    }
                }
            }
        }

        sizeAxisModel = null

        logic.masterDataFrame.addDataFrameListener(object : DataFrameListener<Group, String> {
            override fun dataFrameChanged(event: DataFrameEvent<Group, String>) {
                fireTimeSeriesPlotChanged()
            }
        })

        timeProperty.addPropertyListener(object : PropertyListener<Column?> {
            override fun propertyChanged(event: PropertyEvent<Column?>) {
                timeAxisModel = root.getAxisModel(event.newValue as String)

                fireTimeSeriesPlotChanged()
            }
        })

        yProperty.addPropertyListener(object : PropertyListener<Column?> {
            override fun propertyChanged(event: PropertyEvent<Column?>) {
                yAxisModel = root.getAxisModel(event.newValue as String)

                fireTimeSeriesPlotChanged()
            }
        })

        sizeProperty.addPropertyListener(object : PropertyListener<Column?> {
            override fun propertyChanged(event: PropertyEvent<Column?>) {
                sizeAxisModel = root.getAxisModel(event.newValue as String)

                fireTimeSeriesPlotChanged()
            }
        })

        idProperty.addPropertyListener(object : PropertyListener<Column?> {
            override fun propertyChanged(event: PropertyEvent<Column?>) {
                fireTimeSeriesPlotChanged()
            }
        })

        if (timeAxisModel != null) {
            timeProperty.value = timeAxisModel!!.column
        }
        if (yAxisModel != null) {
            yProperty.value = yAxisModel!!.column
        }

        if (sizeAxisModel != null) {
            sizeProperty.value = sizeAxisModel!!.column
        }

        val name = root.getAxisModel("Name")
        if(name != null) {
            idProperty.value = name?.column
        }
    }


    override val masterVisual: Visual<Group, String>
        get() = logic.getMasterVisual()

    override val masterDataFrame: SubsetDataFrame<Group, String, Any?>
        get() = logic.masterDataFrame

    override fun getMaxDuration(column: Column): Double {
        return logic.getMaxDuration(column).toDouble()
    }

    override fun getMaxEvents(column: Column): Int {
        return logic.getMaxEvents(column)
    }

    override val objectCount: Int
        /**
         * {@inheritDoc}
         */
        get() = dataFrame.rowCount

    override val visual: Visual<Row, Column>
        get() = logic.getVisual()

    override val axisGroupModel: AxisGroupModel<*, *>
        get() = root

    override val probing: MutableSingleSelection<Row>
        /**
         * {@inheritDoc}
         */
        get() = logic.getVisual().probing

    override val selection: MutableSelection<Row>
        /**
         * {@inheritDoc}
         */
        get() = logic.getVisual().selection

    override val filter: Filter<Row>
        /**
         * {@inheritDoc}
         */
        get() = logic.getVisual().filter

    override val colorMapping: MutableColorMapping<Row, Column>
        /**
         * {@inheritDoc}
         */
        get() = logic.getVisual().colorMapping

    override val coloring: MutableColoring<Row>
        /**
         * {@inheritDoc}
         */
        get() = logic.getVisual().coloring

    /**
     * {@inheritDoc}
     */
    override fun getObject(index: Int): Row {
        return dataFrame.getRowKey(index)
    }
}
